// ECharts hook para LiveView
//
// Uso no HEEx (altura via classe Tailwind, nunca style= inline):
//   <div id="my-chart" phx-hook="ECharts"
//        data-option={Jason.encode!(@chart_option)}
//        class="h-[300px]" />
//
// Atualização via push_event do LiveView:
//   push_event(socket, "chart-update", %{id: "my-chart", option: option_map})
//   push_event(socket, "chart-reset", %{id: "my-chart", option: option_map})
//
// chart-update → setOption com merge (animação suave)
// chart-reset  → setOption com notMerge: true (substitui completamente)
//
// Dark mode: o tema `seiva` é construído das CSS custom properties (que
// re-bindam em html.dark). Um MutationObserver na class do <html> re-registra
// o tema e re-inicializa o chart quando o usuário troca light↔dark, reaplicando
// a última option. (Re-init perde zoom/seleção — aceitável em dashboards.)

import * as echarts from "echarts"
import { buildSeivaTheme } from "../seiva_echarts_theme"

const formatUsd = (value) => {
  const amount = Number(value || 0)
  return `US$ ${amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: Math.abs(amount) > 0 && Math.abs(amount) < 0.01 ? 4 : 2,
  })}`
}

const formatDecimal2 = (value) => {
  const amount = Number(value || 0)
  return amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

const reviveFormatters = (option) => {
  if (!option || typeof option !== "object") return option

  if (option.tooltip?.valueFormatter === "usd") {
    option.tooltip.valueFormatter = formatUsd
  }

  const yAxes = Array.isArray(option.yAxis) ? option.yAxis : [option.yAxis]
  yAxes.filter(Boolean).forEach((axis) => {
    if (axis.axisLabel?.formatter === "usd") {
      axis.axisLabel.formatter = formatUsd
    }
    if (axis.axisLabel?.formatter === "decimal2") {
      axis.axisLabel.formatter = formatDecimal2
    }
  })

  return option
}

export default {
  mounted() {
    this.lastOption = reviveFormatters(JSON.parse(this.el.dataset.option || "{}"))
    // Re-register from the live CSS vars at mount: the theme is first registered
    // at bundle import (light), but theme_sync applies the `dark` class slightly
    // later — without this, a hard load in dark mode would init the stale light
    // theme (the observer only fires on a *change*, which never happens here).
    echarts.registerTheme("seiva", buildSeivaTheme())
    this.chart = echarts.init(this.el, "seiva")
    if (Object.keys(this.lastOption).length > 0) this.chart.setOption(this.lastOption)

    // Redimensionar quando o container mudar
    this.resizeObserver = new ResizeObserver(() => this.chart?.resize())
    this.resizeObserver.observe(this.el)

    // Re-tema no toggle dark↔light: observa a class do <html>.
    this.isDark = document.documentElement.classList.contains("dark")
    this.themeObserver = new MutationObserver(() => {
      const dark = document.documentElement.classList.contains("dark")
      if (dark === this.isDark) return
      this.isDark = dark
      this.retheme()
    })
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    })

    // Receber update incremental (merge = animação suave)
    this.handleEvent("chart-update", ({ id, option: opt }) => {
      if (id && id !== this.el.id) return
      this.lastOption = reviveFormatters(opt)
      this.chart?.setOption(opt, { notMerge: false, lazyUpdate: false })
    })

    // Receber reset completo (troca de gráfico)
    this.handleEvent("chart-reset", ({ id, option: opt }) => {
      if (id && id !== this.el.id) return
      this.lastOption = reviveFormatters(opt)
      this.chart?.setOption(opt, { notMerge: true })
    })
  },

  // Re-registra o tema a partir das CSS vars atuais e re-inicializa o chart.
  retheme() {
    if (!this.chart) return
    echarts.registerTheme("seiva", buildSeivaTheme())
    this.chart.dispose()
    this.chart = echarts.init(this.el, "seiva")
    if (this.lastOption && Object.keys(this.lastOption).length > 0) {
      this.chart.setOption(this.lastOption)
    }
  },

  updated() {
    const option = reviveFormatters(JSON.parse(this.el.dataset.option || "{}"))
    if (Object.keys(option).length > 0) {
      this.lastOption = option
      this.chart?.setOption(option, { notMerge: false })
    }
  },

  destroyed() {
    this.resizeObserver?.disconnect()
    this.themeObserver?.disconnect()
    this.chart?.dispose()
  },
}
